import { useState, useEffect, useRef } from 'react';

import Button from '@atlaskit/button/new';

import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';

import { Box } from '@atlaskit/primitives/compiled';

import { Modal } from './OnboardingModal';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useIntl } from 'react-intl';

import { messages } from './messages';

import { cssMap } from '@atlaskit/css';

import Image from '@atlaskit/image';

import { type JSX } from 'react/jsx-runtime';

import { getDocument } from '@atlaskit/browser-apis';

import { MODAL_WIDTH, MODAL_HEIGHT, tourContent } from './contants';

import { type TourContent, type OnboardingModalProps } from './types';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

const styles = cssMap({
	imageWrapper: {
		overflow: 'hidden',
		position: 'relative',
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
});

export const OnboardingModal = ({ onDismiss }: OnboardingModalProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { onAcknowledged, onClosed } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const initialFocusRef = useRef<HTMLButtonElement | null>(null);

	const handleDismiss = () => {
		if (isLastStep) {
			void onAcknowledged({ interactionIdentifier: 'tourCompleted' });
		} else {
			void onClosed({ interactionIdentifier: 'dismiss' });
		}
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'completed',
			actionSubject: 'jiraSoftwareTimelineM3ModalOnboardingDismissed',
			attributes: {
				didCompleteTour: isLastStep,
			},
		});
		onDismiss?.();
	};

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const step: TourContent | undefined = tourContent[currentStepIndex] ?? tourContent[0];
	if (!step) {
		return <></>;
	}

	const isLastStep = currentStepIndex === tourContent.length - 1;

	const handleNextStep = () => {
		if (isLastStep) {
			handleDismiss();
		} else {
			fireAnalyticsEvent({
				eventType: 'track',
				action: 'next',
				actionSubject: 'jiraSoftwareTimelineM3ModalOnboardingNext',
				attributes: {
					currentStep: currentStepIndex,
				},
			});
			setCurrentStepIndex((prev: number) => Math.min(tourContent.length - 1, prev + 1));
		}
	};

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				handleDismiss();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [handleDismiss]);

	return (
		<ModalDialog
			height={MODAL_HEIGHT}
			width={MODAL_WIDTH}
			onOpenComplete={() => {
				fireAnalyticsEvent({
					eventType: 'screen',
					name: 'jiraSoftwareTimelineM3ModalOnboardingViewed',
				});
			}}
			onCloseComplete={() => {
				fireAnalyticsEvent({
					eventType: 'screen',
					name: 'jiraSoftwareTimelineM3ModalOnboardingClosed',
				});
			}}
			testId="softawre-timeline-modal"
			autoFocus={initialFocusRef}
			onClose={handleDismiss}
		>
			<Modal
				title={formatMessage(step.title)}
				content={formatMessage(step.content)}
				sideContent={
					<Box testId="image-wrapper" xcss={styles.imageWrapper}>
						<Image
							style={{
								width: '100%',
								height: '619px',
								flexShrink: 0,
							}}
							src={step.illustration}
							alt=""
						/>
					</Box>
				}
				actions={
					<Button ref={initialFocusRef} onClick={handleNextStep}>
						{formatMessage(isLastStep ? messages.doneButton : messages.nextButton)}
					</Button>
				}
			/>
		</ModalDialog>
	);
};
